import React, { useState, useRef, useEffect } from "react";
import styled from "styled-components";
import { LABEL_PRIMARY } from "@/constants/colors";
import {
  Box,
  Cluster,
  Stack,
  Text,
  ButtonWithAction,
  Dropdown,
} from "@thecb/components";
import { ALL_AGENCIES } from "../administeredUsers/AdministeredUsers.state";

export const DEFAULT_FILTER = "";

export const AGENCIES_FILTER = "agencies";
export const SERVICE_TYPE_FILTER = "serviceType";
export const PAYMENT_STATUS_FILTER = "paymentStatus";
export const PAYMENT_CHANNEL_FILTER = "paymentChannel";
export const PAYMENT_METHOD_FILTER = "paymentMethod";
export const PAYMENT_SOURCE_FILTER = "paymentSource";
export const PAYMENT_MODE_FILTER = "paymentMode";

// Filters that ghenghis (via /payments) now accepts as `param[]=v1&param[]=v2`, returning the OR-union of matches. See the multi-select RMD handoff. The form's `rawValue` for these is a comma-separated string ("" / "v1" / "v1,v2"); the search epic splits it into the array shape qs.stringify renders with brackets. `paymentMode` stays single because its two values are mutually exclusive and exhaustive (selecting both ≡ no filter).
export const MULTI_VALUE_FILTERS = [
  AGENCIES_FILTER,
  PAYMENT_SOURCE_FILTER,
  PAYMENT_STATUS_FILTER,
  PAYMENT_METHOD_FILTER,
  PAYMENT_CHANNEL_FILTER,
  SERVICE_TYPE_FILTER,
];

export const isMultiValueFilter = (key) => MULTI_VALUE_FILTERS.includes(key);

export const FILTER_CATEGORY_DISPLAY_TEXT = {
  [AGENCIES_FILTER]: "Agency",
  [SERVICE_TYPE_FILTER]: "Service type",
  [PAYMENT_STATUS_FILTER]: "Status",
  [PAYMENT_CHANNEL_FILTER]: "Payment channel",
  [PAYMENT_METHOD_FILTER]: "Payment method type",
  [PAYMENT_SOURCE_FILTER]: "Payment source",
  [PAYMENT_MODE_FILTER]: "Payment mode",
};

const PAYMENT_STATUSES = [
  { value: DEFAULT_FILTER, text: "All statuses" },
  ...[
    { value: "voided", text: "Voided" },
    { value: "credited", text: "Overage" },
    { value: "debited", text: "Shortage" },
    { value: "duplicated", text: "Duplicate" },
    { value: "errored", text: "Errored" },
    { value: "expired", text: "Expired" },
    { value: "failed", text: "Failed" },
    { value: "pending", text: "Pending" },
    { value: "refunded", text: "Refund" },
    { value: "rejected", text: "Rejected" },
    { value: "rejected_velocity", text: "Rejected-Velocity" },
    { value: "reversal_needed", text: "Reversal Needed" },
    { value: "successful", text: "Successful" },
    { value: "ach_return", text: "ACH Return" },
    { value: "chargeback", text: "Chargeback" },
    { value: "chargeback_reversal", text: "Chargeback Reversal" },
  ].sort((a, b) => a.text.localeCompare(b.text)),
];

export const FILTERS = {
  [AGENCIES_FILTER]: [{ value: DEFAULT_FILTER, text: ALL_AGENCIES }],
  [SERVICE_TYPE_FILTER]: [{ value: DEFAULT_FILTER, text: "All service types" }],
  [PAYMENT_STATUS_FILTER]: PAYMENT_STATUSES,
  [PAYMENT_CHANNEL_FILTER]: [
    { value: DEFAULT_FILTER, text: "All channels" },
    { value: "kiosk", text: "Kiosk" },
    { value: "cashier", text: "Cashiering" },
    { value: "web", text: "Web" },
  ],
  [PAYMENT_METHOD_FILTER]: [
    { value: DEFAULT_FILTER, text: "All payment methods" },
    { value: "cash", text: "Cash" },
    { value: "check", text: "Check" },
    { value: "credit_card", text: "Credit card" },
    { value: "token", text: "Token" },
    { value: "token_check", text: "Token check" },
    { value: "wire_payment", text: "Wire transfer" },
  ],
  [PAYMENT_SOURCE_FILTER]: [{ value: DEFAULT_FILTER, text: "All payment sources" }],
  [PAYMENT_MODE_FILTER]: [
    { value: DEFAULT_FILTER, text: "All payment modes" },
    { value: "one_time", text: "One-time" },
    { value: "autopay", text: "Autopay" },
  ],
};

const dependentFilters = {
  [AGENCIES_FILTER]: [PAYMENT_SOURCE_FILTER, SERVICE_TYPE_FILTER],
};

const resetDependentFilters = (filter, currentValue, newValue, actions) => {
  if (dependentFilters[filter]) {
    for (const otherFilter of dependentFilters[filter]) {
      if (newValue !== DEFAULT_FILTER && currentValue !== newValue) {
        actions.fields[otherFilter].set(DEFAULT_FILTER);
      }
    }
  }
};

const DropdownContainer = styled.div`
  width: fit-content;
`;

const FilterField = ({ label, field, onSelect, options, describe }) => {
  const [isOpen, setIsOpen] = useState(false);

  const dropdownRef = useRef(null);

  const handleClickAway = (event) => {
    if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
      setIsOpen(false);
    }
  };

  useEffect(() => {
    document.addEventListener("mousedown", handleClickAway);
    return () => {
      document.removeEventListener("mousedown", handleClickAway);
    };
  });

  return (
    <Box padding="8px 16px 0 0">
      <Stack childGap="4px">
        <Text
          variant="pS"
          color={LABEL_PRIMARY}
          weight="400"
          extraStyles={`margin-bottom: 4px;`}
        >
          {describe(label)}
        </Text>
        <DropdownContainer ref={dropdownRef}>
          <Dropdown
            placeholder={options[0].text}
            options={options}
            value={field.rawValue}
            isOpen={isOpen}
            isError={false}
            onSelect={(value) => onSelect(value)}
            onClick={() => setIsOpen(!isOpen)}
            widthFitOptions
            maxHeight="250px"
          />
        </DropdownContainer>
      </Stack>
    </Box>
  );
};

const FiltersForm = ({ actions, fields, filterChoices, descriptionFromKey }) => {
  const filtersEmpty = Object.values(fields).every(
    (field) => field.rawValue === DEFAULT_FILTER
  );

  return (
    <Box padding="16px 0 0 0">
      <Box
        padding="0"
        extraStyles="display: flex; justify-content: space-between; flex-wrap: nowrap;"
      >
        <Box padding="0" extraStyles="display: flex; flex-wrap: wrap;">
          {Object.keys(filterChoices).map((filter) => (
            <FilterField
              key={filter}
              onSelect={(value) => {
                actions.fields?.[filter].set(value);
                resetDependentFilters(
                  filter,
                  fields?.[filter]?.rawValue,
                  value,
                  actions
                );
              }}
              field={fields[filter]}
              label={
                filter === PAYMENT_METHOD_FILTER ? "Payment method type" : filter
              }
              options={filterChoices[filter]}
              describe={descriptionFromKey}
            />
          ))}
        </Box>
        {!filtersEmpty && (
          <Cluster minHeight="100%" align="center" justify="center">
            <ButtonWithAction
              variant="ghost"
              text="Reset filters"
              action={actions.form.clear}
              extraStyles="text-decoration: none !important; flex-basis: 112px;"
              textExtraStyles="font-size: 1rem; font-weight: 400; text-decoration: none !important;"
            />
          </Cluster>
        )}
      </Box>
    </Box>
  );
};

export default FiltersForm;
